/**
 * Lightweight Scroll-Reveal & Animation Controller
 * Uses native IntersectionObserver for zero-lag 60fps animations
 */

class ThemeAnimations {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Respect reduced-motion preference
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.revealAll();
      return;
    }

    this.createObserver();
    this.observeElements();
    this.setupCartListeners();
    this.setupSectionListeners();
    this.setupButtonAnimations();
    this.setupPageTransitions();
  }

  setupPageTransitions() {
    let progressBar = document.getElementById('luxury-top-progress-bar');
    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.id = 'luxury-top-progress-bar';
      progressBar.innerHTML = '<div class="laser-head"></div>';
      document.body.appendChild(progressBar);
    }

    const startProgress = () => {
      progressBar.classList.remove('finished');
      progressBar.classList.remove('active');
      void progressBar.offsetWidth; // Force reflow to restart CSS animation
      progressBar.classList.add('active');
    };

    const completeProgress = () => {
      progressBar.classList.add('finished');
      setTimeout(() => {
        progressBar.classList.remove('active', 'finished');
      }, 500);
    };

    // When clicking Explore Collection CTA or main navigation links
    document.addEventListener('click', (e) => {
      const ctaBtn = e.target.closest('.hero-explore-pill-btn, a[href*="/collections/"], .header__menu-item');
      if (ctaBtn) {
        ctaBtn.classList.add('btn-navigating');
        startProgress();
      }
    });

    window.addEventListener('beforeunload', () => {
      startProgress();
    });

    window.addEventListener('pageshow', () => {
      completeProgress();
      document.querySelectorAll('.btn-navigating').forEach((el) => el.classList.remove('btn-navigating'));
    });
  }

  createObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.08
    };

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, options);
  }

  observeElements(container = document) {
    if (!this.observer) return;

    // Automatically target sections, product cards, collection grids, and marked elements
    const elements = container.querySelectorAll(
      '.theme-animate, [data-animate-scroll], .product-card, .collection-card, .shopify-section:not(#header-group .shopify-section):not(footer .shopify-section)'
    );

    elements.forEach((el, index) => {
      // Don't re-observe already visible elements
      if (el.classList.contains('is-visible')) return;

      // Add base animation class if missing
      if (!el.classList.contains('theme-animate') && !el.hasAttribute('data-animate-scroll')) {
        el.classList.add('theme-animate');
      }

      this.observer.observe(el);
    });
  }

  revealAll() {
    document.querySelectorAll('.theme-animate, [data-animate-scroll]').forEach(el => {
      el.classList.add('is-visible');
    });
  }

  setupCartListeners() {
    const triggerCartBounce = () => {
      const bubbles = document.querySelectorAll('.cart-bubble');
      bubbles.forEach(bubble => {
        bubble.classList.remove('cart-bubble--bounce');
        // Trigger reflow to restart CSS animation
        void bubble.offsetWidth;
        bubble.classList.add('cart-bubble--bounce');
      });
    };

    // Listen to standard cart events and document custom events
    document.addEventListener('cart:update', triggerCartBounce);
    document.addEventListener('cart:item-added', triggerCartBounce);
    document.addEventListener('cart:change', triggerCartBounce);

    // Observe cart bubble changes
    const cartCountEl = document.querySelector('#cart-bubble-text');
    if (cartCountEl && window.MutationObserver) {
      const countObserver = new MutationObserver(() => {
        triggerCartBounce();
      });
      countObserver.observe(cartCountEl, { childList: true, characterData: true, subtree: true });
    }
  }

  setupSectionListeners() {
    // Support Shopify Theme Editor section re-rendering & SPA view transitions
    document.addEventListener('shopify:section:load', (e) => {
      this.observeElements(e.target);
    });

    document.addEventListener('page:loaded', () => {
      this.observeElements();
    });
  }

  setupButtonAnimations() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quick-add__button, .add-to-cart-button');
      if (!btn) return;

      // Create dynamic ripple wave at click point
      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.classList.add('btn-ripple');
      const size = Math.max(rect.width, rect.height) * 2;
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
      btn.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);

      // Trigger .added burst & checkmark state
      btn.classList.add('added');
      if (btn._addedTimeout) clearTimeout(btn._addedTimeout);
      btn._addedTimeout = setTimeout(() => {
        btn.classList.remove('added');
      }, 1800);
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.themeAnimations = new ThemeAnimations();
  });
} else {
  window.themeAnimations = new ThemeAnimations();
}

export { ThemeAnimations };
